/**
 * TypeScript types and interfaces for Policy Management Module
 * 
 * This file contains type definitions for the policy management system including:
 * - Policy CRUD operations
 * - Document management
 * - Commission tracking
 * - Status history
 * - Dashboard metrics
 * - Renewal operations
 * 
 * Requirements: Policy Management Frontend Design Document
 */

import type { PremiumItem } from './quotation';
export type { PremiumItem };

// ============================================================================
// Enums
// ============================================================================

/**
 * Policy status enum
 */
export enum PolicyStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  CANCELLED = 'cancelled',
  PENDING = 'pending',
}

/**
 * Policy type enum
 */
export enum PolicyTypeEnum {
  LIFE = 'life',
  MOTOR = 'motor',
  HEALTH = 'health',
  PROPERTY = 'property',
  FIRE = 'fire',
  MARINE = 'marine',
  MISC = 'misc',
}

/**
 * Commission status enum
 */
export enum PolicyCommissionStatus {
  PENDING = 'unpaid',       // backend stores as 'unpaid'
  PAID = 'paid',
  PARTIALLY_PAID = 'partially_paid',
}

// ============================================================================
// Main Policy Interface
// ============================================================================

/**
 * Main policy interface
 */
export interface Policy {
  id: string;
  policy_number: string;
  policy_type: PolicyTypeEnum;
  is_internal: boolean;

  // Relationships
  customer_id: string;
  customer_name: string;
  agent_id: string;
  agent_name: string;
  insurance_company_id: string;
  insurance_company_name: string;
  team_id?: string;
  team_name?: string;

  // Financial information
  premium_amount: number;
  premiums?: PremiumItem[];
  commission_rate: number;
  commission_amount: number;
  commission_status: PolicyCommissionStatus;
  commission_paid_date?: string;
  commission_paid_amount: number;

  // Dates and expiry
  start_date: string;
  end_date: string;
  days_until_expiry?: number;
  is_nearing_expiry: boolean;

  // Status and cancellation
  status: PolicyStatus;
  cancellation_date?: string;
  cancellation_reason?: string;

  // Renewal tracking
  original_policy_id?: string;
  renewed_policy_id?: string;

  // Additional information
  policy_type_detail?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  is_deleted: boolean;
}

// ============================================================================
// Policy Document Interface
// ============================================================================

/**
 * Policy document interface
 */
export interface PolicyDocument {
  id: string;
  policy_id: string;
  filename: string;
  original_filename: string;
  file_type: string;
  file_size: number;
  file_path: string;
  content_type: string;
  uploaded_by: string;
  uploaded_at: string;
}

// ============================================================================
// Policy Status History Interface
// ============================================================================

/**
 * Policy status history interface
 */
export interface PolicyStatusHistory {
  id: string;
  policy_id: string;
  old_status?: PolicyStatus;
  new_status: PolicyStatus;
  changed_by: string;
  changed_by_name: string;
  changed_at: string;
  change_reason?: string;
}

// ============================================================================
// Policy Detail Interface
// ============================================================================

/**
 * Source quotation reference (minimal info for linking)
 */
export interface PolicySourceQuotation {
  id: string;
  quote_number: string;
  converted_at?: string;
}

/**
 * Policy detail with documents and history
 */
export interface PolicyDetail extends Policy {
  documents: PolicyDocument[];
  status_history: PolicyStatusHistory[];
  source_quotation?: PolicySourceQuotation | null;
}

// ============================================================================
// Dashboard Metrics Interface
// ============================================================================

/**
 * Dashboard metrics interface
 */
export interface DashboardMetrics {
  active_policies_count: number;
  monthly_premium_total: number;
  unpaid_commission_total: number;
  policies_nearing_expiry_count: number;
  policies_by_type: Record<string, number>;
  premium_trends: Array<{ month: string; total: number }>;
  top_agents: Array<{ agent_name: string; total_premium: number; policy_count: number }>;
  // Quotation metrics
  total_quotations_count: number;
  draft_quotations_count: number;
  recent_quotations: Array<{
    id: string;
    quote_number: string;
    customer_name: string;
    customer_type: string;
    insurance_type: string;
    status: string;
    quote_date: string | null;
    details: string;
    created_at: string | null;
  }>;
  // Company sales breakdown
  sales_by_company: Array<{
    company: string;
    total: number;
    by_type: Record<string, number>;
  }>;
}

// ============================================================================
// Form Data Interfaces
// ============================================================================

/**
 * Policy form data for create/update operations
 */
export interface PolicyFormData {
  policy_number: string;
  policy_type: PolicyTypeEnum;
  is_internal?: boolean;
  customer_id: string;
  agent_id: string;
  insurance_company_id: string;
  team_id?: string;
  premium_amount?: number;
  premiums?: PremiumItem[];
  commission_rate: number;
  start_date: string;
  end_date: string;
  policy_type_detail?: string;
  notes?: string;
}

/**
 * Policy renewal creation data
 */
export interface PolicyRenewalCreate {
  original_policy_id: string;
  policy_number?: string;
  premium_amount?: number;
  commission_rate?: number;
  start_date?: string;
  end_date?: string;
  notes?: string;
}

/**
 * Commission payment update data
 */
export interface CommissionPaymentUpdate {
  commission_status: PolicyCommissionStatus;
  commission_paid_amount: number;
  commission_paid_date: string;
}

/**
 * Policy status update data
 */
export interface PolicyStatusUpdate {
  status: PolicyStatus;
  change_reason?: string;
  cancellation_date?: string;
  cancellation_reason?: string;
}

// ============================================================================
// Component Props Interfaces
// ============================================================================

/**
 * Props for PolicyDocuments component
 */
export interface PolicyDocumentsProps {
  policyId: string;
  documents: PolicyDocument[];
  onUpload: (file: File) => Promise<void>;
  onDownload: (documentId: string) => Promise<void>;
  onDelete: (documentId: string) => Promise<void>;
}

/**
 * Props for CommissionUpdateDialog component
 */
export interface CommissionUpdateDialogProps {
  open: boolean;
  policy: Policy;
  onClose: () => void;
  onUpdate: (data: CommissionPaymentUpdate) => Promise<void>;
}

/**
 * Props for RenewalDialog component
 */
export interface RenewalDialogProps {
  open: boolean;
  originalPolicy: Policy;
  onClose: () => void;
  onRenew: (data: PolicyRenewalCreate) => Promise<void>;
}

/**
 * Props for StatusUpdateDialog component
 */
export interface StatusUpdateDialogProps {
  open: boolean;
  policy: Policy;
  onClose: () => void;
  onUpdate: (data: PolicyStatusUpdate) => Promise<void>;
}

/**
 * Props for PolicyDashboard component
 */
export interface PolicyDashboardProps {
  metrics: DashboardMetrics;
  loading: boolean;
}

// ============================================================================
// Import/Export Interfaces
// ============================================================================

/**
 * Policy import result
 */
export interface PolicyImportResult {
  success: number;
  failed: number;
  errors: string[];
}

// ============================================================================
// Filter Interfaces
// ============================================================================

/**
 * Policy filter parameters
 */
export interface PolicyFilters {
  policyType?: string;
  status?: string;
  commissionStatus?: string;
  startDateFrom?: string;
  startDateTo?: string;
}
